import React, { Component } from 'react';

import SidebarComponent from '../components/SidebarComponent';
import NavbarComponent from '../components/NavbarComponent';
import HeaderComponent from '../components/HeaderComponent';
import TournamentTableCard from '../components/TournamentTableCard';
import RankChainEditor from '../components/RankChainEditor';
import ScoreTableEditor from '../components/ScoreTableEditor';
import TiebreakModal from '../components/TiebreakModal';
import SeatingModal from '../components/SeatingModal';
import ParticipantEditModal from '../components/ParticipantEditModal';
import { Aviso } from '../components/TournamentOverlay';
import { validateSession } from '../utils/functions';
import { navigateTo } from '../utils/navigation';
import * as api from '../utils/tournamentsApi';
import { ESTADO_TORNEO, ESTADO_RONDA, MOTIVO_BAJA, fechaEvento } from '../utils/tournamentsApi';
import {
    CRITERIOS, PLAN_CATAN, ACCIONES_BITACORA, parseJson, etapasDe, tieneEtapa, totalPreliminares,
    describirEtapas, opcionesDeReparto,
} from '../utils/tournamentFormats';
import { createBlock, getBlocks, getScreens, tipoDe } from '../utils/cartelera';
import QRCode from 'qrcode';

const config = process.env;

// La tabla pública vive en el STOREFRONT (spec OPEN-2): el link se comparte por WhatsApp y se
// proyecta en el salón, así que tiene que ser la URL del sitio.
//
// Antes se armaba con REACT_APP_REDIRECT_LOGIN "porque es la base del storefront". Eso es cierto
// SÓLO en local (localhost:3000); en staging y prod esa variable apunta al BACKOFFICE, así que el
// link que se le pasaba a los jugadores era `admin.enigmagames.uy/torneo/...` — un dominio que ni
// siquiera tiene esa ruta. Por eso ahora hay una variable propia y explícita.
const urlPublica = (token) => {
    const base = (config.REACT_APP_STOREFRONT_URL || '').replace(/\/+$/, '');
    return `${base}/torneo/${token}`;
};

// Pantalla de un torneo: configuración antes de empezar, control de puntajes durante, y
// posiciones. Convención del repo: el id viaja por query string (?id=NN), como editEvent.
class TournamentPage extends Component {

    constructor(props) {
        const cachedRole = Number(localStorage.getItem('role') || 0);
        super(props);
        const params = new URLSearchParams(props.location.search);
        this.state = {
            checked: !!localStorage.getItem('token'),
            role: cachedRole,
            id: params.get('id'),
            tab: 'config',
            draft: {},
            guardado: false,
            loading: true,
            error: null,
            tournament: null,
            participants: [],
            rounds: [],
            preview: null,
            roundView: null,      // id de la ronda que se está mirando
            roundData: null,
            standings: null,
            finalRanking: null,
            nombresPegados: '',
            busy: false,
            sessions: [],
            presentes: null,   // selección en curso al marcar asistencia
            lista: null,       // pasar lista en un torneo de un día (selección en curso)
            contacts: {},
            log: null,
            ties: [],
            chain: [],
            declared: false,
            stageBuilt: null,
            modalEmpate: null,   // grupo empatado que se está resolviendo
            modalMesas: null,    // { modo, mesas, roundId?, numero? }
            modalJugador: null,  // participante que se está editando
            exportCheck: null,
            qr: null,
            tv: null,            // { screens, bloques: [{ screen, block }] } — dónde está este torneo en la cartelera
            tvPantalla: null,    // pantalla elegida para agregarlo (si hay más de una)
            event: null,         // el evento del que cuelga, si cuelga de uno
            eventosEnganchables: null,
            eventoElegido: '',
        };
    }

    async componentDidMount() {
        const role = await validateSession(true);
        this.setState({ checked: true, role });
        if (!this.state.id) { window.alert('Falta el torneo.'); navigateTo('/tournaments'); return; }
        this.cargar();
    }

    componentWillUnmount() { this._muerto = true; }

    set = (patch) => { if (!this._muerto) this.setState(patch); }

    // Sólo la PRIMERA carga pone "Cargando…". Antes cada acción —y cada "Confirmar mesa"— ponía
    // `loading` y el render reemplazaba la pestaña entera: la grilla de mesas se desmontaba y
    // volvía a montar, y cualquier mesa a medio cargar en otra tarjeta perdía los toques. Con 24
    // anotados son 18 mesas: en el salón se leía como "la pantalla se cuelga".
    cargar = async (opciones = {}) => {
        if (!this.state.tournament) this.set({ loading: true });
        this.set({ error: null });
        try {
            const data = await api.getTournament(this.state.id);
            const rounds = data.rounds || [];
            // por default se mira la última ronda que exista
            const ultima = rounds.length ? rounds[rounds.length - 1].id : null;
            const roundView = opciones.roundView === null
                ? ultima                                   // acción que creó una ronda: saltar a ella
                : (this.state.roundView && rounds.some((r) => r.id === this.state.roundView)
                    ? this.state.roundView
                    : ultima);

            // Un torneo que ya empezó se abre en "Rondas y mesas": la configuración importa
            // mucho ANTES de arrancar y casi nada después, y en el salón el que entra a la
            // pantalla viene a cargar una mesa, no a mirar el formato. Sólo la primera carga
            // decide: después manda lo que el usuario haya elegido.
            const tab = this.tabElegido || (data.tournament.status === 'setup' ? 'config' : 'rondas');
            this.tabElegido = this.tabElegido || tab;

            this.set({
                tournament: data.tournament,
                event: data.event || null,
                participants: data.participants || [],
                rounds,
                roundView,
                tab,
                loading: false,
            });
            const esLiga = data.tournament.sessions_planned != null;
            const [preview, standings, sesiones, contactos] = await Promise.all([
                api.getPreview(this.state.id).catch(() => null),
                api.getStandings(this.state.id).catch(() => null),
                esLiga ? api.getSessions(this.state.id).catch(() => null) : Promise.resolve(null),
                api.getContacts(this.state.id).catch(() => null),
            ]);
            this.set({
                preview,
                standings: standings ? standings.standings : null,
                finalRanking: standings ? standings.finalRanking : null,
                ties: standings ? (standings.ties || []) : [],
                chain: standings ? (standings.chain || []) : [],
                declared: standings ? !!standings.declared : false,
                stageBuilt: standings ? standings.stageBuilt : null,
                sessions: sesiones ? sesiones.sessions : [],
                contacts: contactos ? contactos.contacts : {},
            });
            if (roundView) await this.cargarRonda(roundView);
            if (this.state.log) this.cargarBitacora();
        } catch (e) {
            this.set({ error: e.message, loading: false });
        }
    }

    cargarRonda = async (rid) => {
        try {
            const data = await api.getRound(this.state.id, rid);
            this.set({ roundView: rid, roundData: data });
        } catch (e) { window.alert(e.message); }
    }

    // Toda acción que muta pasa por acá: un solo lugar donde se muestra el error de negocio
    // y se recarga, en vez de repetir try/catch en cada botón.
    // `saltarAUltima` es para las acciones que CREAN una ronda: sin eso, cargar() preserva la
    // ronda que se estaba mirando y la pantalla se queda en la anterior con todo cargado, que
    // se lee como "el botón no hizo nada".
    accion = async (fn, opciones = {}) => {
        if (opciones.confirmacion && !window.confirm(opciones.confirmacion)) return;
        this.set({ busy: true });
        try {
            const r = await fn();
            await this.cargar(opciones.saltarAUltima ? { roundView: null } : {});
            if (opciones.despues) opciones.despues(r);
        } catch (e) {
            // Un empate en el corte trae el grupo: se abre directo "Resolver empate" en vez de
            // dejar al juez con un mensaje y buscando dónde se resuelve.
            const tie = e.body && e.body.data && e.body.data.details && e.body.data.details.tie;
            if (tie) {
                window.alert(e.message);
                this.set({ modalEmpate: tie });
            } else {
                window.alert(e.message);
            }
        } finally {
            this.set({ busy: false });
        }
    }

    cargarBitacora = async () => {
        try {
            const r = await api.getLog(this.state.id);
            this.set({ log: r.log || [] });
        } catch (e) { this.set({ log: [] }); }
    }

    rowsById = () => {
        const out = {};
        (this.state.standings || []).forEach((r) => { out[r.participantId] = r; });
        return out;
    }

    guardarEmpate = async (orden, clear) => {
        await api.setTiebreak(this.state.id, orden, clear);
        this.set({ modalEmpate: null });
        await this.cargar();
    }

    renderModales() {
        const t = this.state.tournament;
        return (
            <React.Fragment>
                {this.state.modalEmpate ? (
                    <TiebreakModal group={this.state.modalEmpate} rowsById={this.rowsById()}
                        onClose={() => this.set({ modalEmpate: null })} onSave={this.guardarEmpate} />
                ) : null}
                {this.state.modalMesas ? (
                    <SeatingModal {...this.state.modalMesas}
                        onClose={() => this.set({ modalMesas: null })}
                        onSave={this.guardarMesas} />
                ) : null}
                {this.state.modalJugador && t ? (
                    <ParticipantEditModal participant={this.state.modalJugador}
                        contact={this.state.contacts[this.state.modalJugador.id]}
                        grupos={[...new Set(this.state.participants.map((p) => p.affinity_group).filter(Boolean))]}
                        onClose={() => this.set({ modalJugador: null })}
                        onSave={async (payload) => {
                            await api.updateParticipant(this.state.id, this.state.modalJugador.id, payload);
                            this.set({ modalJugador: null });
                            await this.cargar();
                        }} />
                ) : null}
            </React.Fragment>
        );
    }

    // ───────────────────────────── configuración

    // La configuración se editaba campo por campo, con un PATCH al salir del foco y sin ningún
    // aviso de que algo se hubiera guardado. Funcionaba, pero no había forma de saberlo: la
    // pantalla se leía como un borrador que nunca se confirmó —"¿cómo guardo el torneo?"— y
    // además un cambio a medias podía chocar con una validación que mira el estado FINAL (poner
    // "sale último" en 8 antes de subir "gana la mesa" a 10 es inválido en el paso intermedio y
    // válido al terminar).
    //
    // Ahora se edita un borrador local y se manda todo junto. El botón dice si hay algo sin
    // guardar, y guardar deja una marca visible.
    campoConfig = (k, v) => this.setState({ draft: { ...(this.state.draft || {}), [k]: v }, guardado: false });

    valorConfig = (k, actual) => {
        const d = this.state.draft || {};
        return d[k] !== undefined ? d[k] : actual;
    }

    haySinGuardar = () => Object.keys(this.state.draft || {}).length > 0;

    guardarConfig = (patch) => this.accion(async () => {
        // Se limpian SÓLO las claves que se mandaron, no el borrador entero: si seguís tipeando
        // mientras viaja el PATCH, lo nuevo sobrevive en vez de retroceder al valor enviado.
        const enviado = patch || this.state.draft;
        await api.updateTournament(this.state.id, enviado);
        const quedan = { ...(this.state.draft || {}) };
        Object.keys(enviado).forEach((k) => { delete quedan[k]; });
        this.set({ draft: quedan, guardado: true });
        clearTimeout(this.avisoGuardado);
        this.avisoGuardado = setTimeout(() => this.set({ guardado: false }), 4000);
    });

    traerInscriptos = () => this.accion(async () => {
        const r = await api.syncParticipants(this.state.id);
        const partes = [r.created === 1 ? 'Se trajo 1 inscripto.' : `Se trajeron ${r.created} inscriptos.`];
        if (r.matched) {
            partes.push(r.matched === 1
                ? '1 ya estaba cargado a mano con el mismo nombre: se le vinculó su entrada en vez de duplicarlo.'
                : `${r.matched} ya estaban cargados a mano con el mismo nombre: se les vinculó su entrada en vez de duplicarlos.`);
        }
        if (r.created === 0 && !r.matched) partes.push('No había ninguno nuevo.');
        // El que compró sin dejar datos no tiene cuenta, y sin cuenta no hay historial:
        // conviene que el staff lo sepa AHORA, cuando todavía puede pedirle el mail.
        if (r.withoutAccount) {
            partes.push(`${r.withoutAccount} sin cuenta: van a jugar igual, pero el torneo no les va a quedar en el historial.`);
        }
        window.alert(partes.join(' '));
    });

    pegarNombres = () => {
        const nombres = this.state.nombresPegados.split('\n');
        this.accion(async () => {
            const r = await api.addParticipantsBulk(this.state.id, nombres);
            this.set({ nombresPegados: '' });
            if (r.skipped) window.alert(`Se agregaron ${r.created}. ${r.skipped} ya estaban en la lista.`);
        });
    }

    cambiarEstadoJugador = (p) => this.accion(
        () => api.updateParticipant(this.state.id, p.id, { status: p.status === 'active' ? 'dropped' : 'active' }),
    );

    // Guardar la configuración valida antes lo que el backend va a rechazar igual, pero con el
    // campo a la vista: una tabla de puntos incompleta, sobre todo.
    guardarFormato = () => {
        const d = this.state.draft || {};
        const t = this.state.tournament;
        const envio = { ...d };
        // El editor de la tabla edita un objeto: se manda entero sólo si se tocó.
        if (envio.scoringMode === 'formula') envio.scoreTable = null;
        if (d.scoringMode === 'table' && d.scoreTable === undefined) envio.scoreTable = parseJson(t.score_table) || {};
        this.guardarConfig(envio);
    }

    renderFormato() {
        const t = this.state.tournament;
        const prev = this.state.preview;
        const enArmado = t.status === 'setup';
        const esLiga = this.valorConfig('sessionsPlanned', t.sessions_planned) != null && this.valorConfig('sessionsPlanned', t.sessions_planned) !== '';
        const scoringMode = this.valorConfig('scoringMode', t.scoring_mode);
        const tabla = this.valorConfig('scoreTable', parseJson(t.score_table));
        const rankBy = this.valorConfig('rankBy', parseJson(t.rank_by));
        const plan = this.valorConfig('bracketPlan', parseJson(t.bracket_plan));
        const planCongelado = !!(parseJson(t.bracket_plan) || {}).resolved;
        const finalSize = this.valorConfig('finalTableSize', t.final_table_size);

        return (
            <div className="card card-small mb-4">
                {/* El botón va en el ENCABEZADO y no al pie: la tarjeta es larga y al pie queda
                    fuera de la vista —no encontrar cómo guardar. */}
                <div className="card-header border-bottom d-flex justify-content-between align-items-center">
                    <h6 className="m-0">Formato</h6>
                    <div className="d-flex align-items-center" style={{ gap: 9 }}>
                        {this.state.guardado ? (
                            <span className="badge badge-success">Guardado</span>
                        ) : this.haySinGuardar() ? (
                            <span className="badge badge-warning">Sin guardar</span>
                        ) : null}
                        {this.haySinGuardar() ? (
                            <button type="button" className="btn btn-sm btn-white" disabled={this.state.busy}
                                onClick={() => this.set({ draft: {} })}>Descartar</button>
                        ) : null}
                        <button type="button" className="btn btn-sm btn-accent"
                            disabled={this.state.busy || !this.haySinGuardar()}
                            onClick={this.guardarFormato}>Guardar cambios</button>
                    </div>
                </div>
                <div className="card-body">
                    {!esLiga && finalSize == null && !(plan && tieneEtapa({ ...t, bracket_plan: plan, final_table_size: finalSize }, 'final', prev && prev.players)) ? (
                        <Aviso tipo="error">
                            <strong>Este torneo no tiene mesa final.</strong> Al cerrar la última ronda termina y gana
                            el primero de la tabla, sin confirmación. Si hay final, elegila abajo — se puede con el
                            torneo empezado, mientras no se cierre la última ronda.
                        </Aviso>
                    ) : null}
                    {enArmado ? null : (
                        <Aviso tipo="warn">
                            El torneo ya empezó: el tamaño de mesa, las rondas, el puntaje y las etapas quedan fijos. La
                            mesa final y el orden de la tabla se pueden cambiar (queda registrado en la bitácora).
                        </Aviso>
                    )}
                    {prev && prev.format ? (
                        <Aviso tipo="info">
                            <strong>Formato: {prev.format.description}.</strong>
                            {prev.format.tierLabel ? ` Tramo de ${prev.format.tierLabel}${prev.format.frozen ? ' (fijado al arrancar).' : ', según los anotados de hoy: se fija al generar la ronda 1.'}` : ''}
                        </Aviso>
                    ) : null}
                    <div className="form-group">
                        <label>Juego</label>
                        <input type="text" className="form-control" value={this.valorConfig('gameName', t.game_name || '')}
                            onChange={(e) => this.campoConfig('gameName', e.target.value)} />
                    </div>
                    <div className="row">
                        <div className="col-4 form-group">
                            <label>Jugadores por mesa</label>
                            <select className="form-control" value={this.valorConfig('tableSize', t.table_size)} disabled={!enArmado}
                                onChange={(e) => this.campoConfig('tableSize', Number(e.target.value))}>
                                {[2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </div>
                        <div className="col-4 form-group">
                            <label>Mínimo por mesa</label>
                            <select className="form-control" value={this.valorConfig('minTableSize', t.min_table_size)} disabled={!enArmado}
                                onChange={(e) => this.campoConfig('minTableSize', Number(e.target.value))}>
                                {[2, 3, 4, 5, 6].filter((n) => n <= Number(this.valorConfig('tableSize', t.table_size))).map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </div>
                        <div className="col-4 form-group">
                            <label>{esLiga ? 'Rondas por jornada' : 'Rondas'}</label>
                            <input type="number" className="form-control" min="1" max="12"
                                value={this.valorConfig('roundsPlanned', t.rounds_planned)} disabled={!enArmado}
                                onChange={(e) => this.campoConfig('roundsPlanned', e.target.value)} />
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Tipo</label>
                        <select className="form-control" disabled={!enArmado}
                            value={String(this.valorConfig('sessionsPlanned', t.sessions_planned == null ? '' : t.sessions_planned))}
                            onChange={(e) => this.campoConfig('sessionsPlanned', e.target.value === '' ? null : Number(e.target.value))}>
                            <option value="">Torneo de un día</option>
                            {[4, 6, 8, 10, 12, 16].map((n) => <option key={n} value={n}>Liga de {n} jornadas</option>)}
                        </select>
                        {esLiga ? (
                            <small className="text-muted">
                                En una liga, "Rondas" son las rondas de <strong>cada jornada</strong> (2 por martes).
                                La tabla general suma todo lo jugado, así que faltar cuesta puntos.
                            </small>
                        ) : null}
                    </div>

                    {esLiga ? null : (
                        <div className="form-group">
                            <label>Mesa final</label>
                            <select className="form-control" disabled={!!(plan && plan.tiers)}
                                value={String(finalSize == null ? '' : finalSize)}
                                onChange={(e) => this.campoConfig('finalTableSize', e.target.value === '' ? null : Number(e.target.value))}>
                                <option value="">Sin mesa final</option>
                                {[3, 4, 5, 6].map((n) => <option key={n} value={n}>Los {n} mejores</option>)}
                            </select>
                            <small className="text-muted">
                                {plan && plan.tiers
                                    ? 'Con el formato por cantidad de anotados, la semifinal y la final las define el tramo (abajo).'
                                    : 'La mesa final define el podio y no otorga puntos: así “hice 18 puntos” y “salí 2°” conviven sin contradecirse.'}
                            </small>
                        </div>
                    )}

                    <div className="form-group">
                        <label>Puntaje</label>
                        <select className="form-control" value={scoringMode} disabled={!enArmado}
                            onChange={(e) => this.campoConfig('scoringMode', e.target.value)}>
                            <option value="formula">Por puesto (fórmula lineal)</option>
                            <option value="table">Por tabla (cuánto paga cada puesto)</option>
                        </select>
                    </div>
                    {scoringMode === 'table' ? (
                        <div className="form-group">
                            <ScoreTableEditor value={tabla} disabled={!enArmado}
                                min={this.valorConfig('minTableSize', t.min_table_size)}
                                max={this.valorConfig('tableSize', t.table_size)}
                                onChange={(v) => this.campoConfig('scoreTable', v)} />
                            <small className="text-muted">Con 1 al que gana y 0 al resto, los puntos del torneo son las victorias (bases de Catan).</small>
                        </div>
                    ) : (
                        <div className="row">
                            <div className="col-6 form-group">
                                <label>Gana la mesa</label>
                                <input type="number" className="form-control" value={this.valorConfig('scoreMax', t.score_max)}
                                    disabled={!enArmado} onChange={(e) => this.campoConfig('scoreMax', e.target.value)} />
                            </div>
                            <div className="col-6 form-group">
                                <label>Sale último</label>
                                <input type="number" className="form-control" value={this.valorConfig('scoreMin', t.score_min)}
                                    disabled={!enArmado} onChange={(e) => this.campoConfig('scoreMin', e.target.value)} />
                            </div>
                        </div>
                    )}
                    <div className="form-group">
                        <label className="d-flex align-items-center" style={{ gap: 8 }}>
                            <input type="checkbox" checked={!!this.valorConfig('usesRawScore', !!t.uses_raw_score)}
                                onChange={(e) => this.campoConfig('usesRawScore', e.target.checked)} />
                            Se anota el puntaje del juego (PV)
                        </label>
                    </div>

                    <div className="form-group">
                        <label>Orden de la tabla</label>
                        <RankChainEditor value={rankBy} onChange={(v) => this.campoConfig('rankBy', v)} />
                        {enArmado ? null : (
                            <small className="text-muted">Cambiarlo con el torneo andando reordena la tabla al instante y queda en la bitácora.</small>
                        )}
                    </div>

                    {esLiga ? null : (
                        <div className="form-group">
                            <label className="d-flex align-items-center" style={{ gap: 8 }}>
                                <input type="checkbox" checked={!!plan} disabled={!enArmado}
                                    onChange={(e) => this.campoConfig('bracketPlan', e.target.checked ? PLAN_CATAN : null)} />
                                Formato por cantidad de anotados (bases Catan: semifinal de 16 desde 29)
                            </label>
                            {plan && plan.tiers ? (
                                <small className="text-muted">
                                    {plan.tiers.map((tr) => `${tr.label}: ${describirEtapas(this.valorConfig('roundsPlanned', t.rounds_planned), tr.stages)}`).join(' · ')}.
                                    {planCongelado ? ' El tramo ya quedó fijo.' : ' El tramo se fija al generar la ronda 1, con los presentes.'}
                                </small>
                            ) : null}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ───────────────────────────── evento

    // Un torneo y un evento se pueden armar por separado —el torneo con su formato y su lista a
    // mano, el evento con la venta de entradas— y engancharse después. Antes la única forma era
    // CREAR el torneo desde el evento, y un torneo ya armado no se podía juntar con nada.
    abrirEnganche = async () => {
        try {
            const r = await api.getLinkableEvents(this.state.id);
            this.set({ eventosEnganchables: r.events || [], eventoElegido: '' });
        } catch (e) { window.alert(e.message); }
    }

    engancharEvento = () => {
        const eventId = Number(this.state.eventoElegido);
        if (!eventId) return;
        this.accion(async () => {
            const r = await api.linkEvent(this.state.id, eventId);
            this.set({ eventosEnganchables: null });
            return r;
        }, {
            despues: (r) => window.alert(`Listo: el torneo quedó enganchado a "${r.eventTitle}". Tocá "Traer inscriptos del evento" `
                + 'para sumar a los que compraron entrada (a los que ya cargaste a mano con el mismo nombre se les vincula la entrada, no se duplican).'),
        });
    }

    desengancharEvento = () => this.accion(() => api.unlinkEvent(this.state.id), {
        confirmacion: 'El torneo vuelve a ser suelto. Los participantes que vinieron del evento se quedan. ¿Desenganchar?',
    })

    renderEvento() {
        const ev = this.state.event;
        const lista = this.state.eventosEnganchables;
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom"><h6 className="m-0">Evento</h6></div>
                <div className="card-body" style={{ fontSize: 13 }}>
                    {ev ? (
                        <div className="d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
                            <span>Enganchado a <strong>{ev.title}</strong>{ev.startsAt ? ` · ${fechaEvento(ev.startsAt)}` : ''}</span>
                            <button type="button" className="btn btn-sm btn-white" onClick={() => navigateTo(`/editEvent?id=${ev.id}`)}>Ir al evento</button>
                            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={this.state.busy}
                                onClick={this.desengancharEvento}>Desenganchar</button>
                        </div>
                    ) : lista == null ? (
                        <div className="d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
                            <span className="text-muted">Torneo suelto: no cuelga de ningún evento.</span>
                            <button type="button" className="btn btn-sm btn-white" onClick={this.abrirEnganche}>Enganchar a un evento</button>
                        </div>
                    ) : lista.length ? (
                        <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                            <select className="form-control form-control-sm" style={{ width: 'auto', maxWidth: 360 }} aria-label="Evento a enganchar"
                                value={this.state.eventoElegido} onChange={(e) => this.set({ eventoElegido: e.target.value })}>
                                <option value="">Elegí un evento…</option>
                                {lista.map((e) => (
                                    <option key={e.id} value={e.id}>{e.title}{e.startsAt ? ` · ${fechaEvento(e.startsAt)}` : ''}{e.status === 'draft' ? ' (borrador)' : ''}</option>
                                ))}
                            </select>
                            <button type="button" className="btn btn-sm btn-accent" disabled={this.state.busy || !this.state.eventoElegido}
                                onClick={this.engancharEvento}>Enganchar</button>
                            <button type="button" className="btn btn-sm btn-link p-0" onClick={() => this.set({ eventosEnganchables: null })}>Cancelar</button>
                            <div className="text-muted w-100" style={{ fontSize: 12 }}>
                                Sólo aparecen los eventos sin torneo. Después, "Traer inscriptos" suma a los que compraron entrada.
                            </div>
                        </div>
                    ) : (
                        <span className="text-muted">No hay eventos sin torneo para enganchar. <button type="button" className="btn btn-sm btn-link p-0"
                            onClick={() => this.set({ eventosEnganchables: null })}>Volver</button></span>
                    )}
                </div>
            </div>
        );
    }

    renderParticipantes() {
        const t = this.state.tournament;
        const prev = this.state.preview;
        const activos = this.state.participants.filter((p) => p.status === 'active');
        const contactos = this.state.contacts || {};
        const oficial = this.state.declared;
        const sinInformar = this.state.participants.filter((p) => !p.consent_at).length;
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom d-flex justify-content-between align-items-center">
                    <h6 className="m-0">Participantes ({activos.length})</h6>
                    {oficial && sinInformar ? <span className="badge badge-warning">{sinInformar} sin informar</span> : null}
                </div>
                <div className="card-body">
                    {t.event_id ? (
                        <div style={{ marginBottom: 14 }}>
                            <button type="button" className="btn btn-sm btn-accent" disabled={this.state.busy}
                                onClick={this.traerInscriptos}>Traer inscriptos del evento</button>
                            <div className="text-muted" style={{ fontSize: 12.5, marginTop: 7, lineHeight: 1.45 }}>
                                Trae a los que tienen la entrada válida o ya canjeada. Se puede tocar de nuevo
                                cuando llegue gente: no duplica a nadie.
                            </div>
                        </div>
                    ) : null}

                    <div className="form-group">
                        <label>{t.event_id ? 'Agregar a mano (staff que completa una mesa, o alguien que llegó tarde)' : 'Pegá los nombres, uno por línea'}</label>
                        <textarea className="form-control" rows={t.event_id ? 2 : 5} value={this.state.nombresPegados}
                            placeholder={'Juan Pérez\nAna Gómez\n…'}
                            onChange={(e) => this.setState({ nombresPegados: e.target.value })} />
                    </div>
                    <button type="button" className="btn btn-sm btn-white" disabled={this.state.busy || !this.state.nombresPegados.trim()}
                        onClick={this.pegarNombres}>Agregar</button>
                    {t.status !== 'setup' ? (
                        <span className="text-muted ml-2" style={{ fontSize: 12.5 }}>
                            Con el torneo empezado, el que se agrega juega desde la próxima ronda y la que no jugó le cuenta 0.
                        </span>
                    ) : null}

                    {prev && prev.split && t.sessions_planned != null ? (
                        <Aviso tipo={prev.split.canAvoid ? 'info' : 'warn'} style={{ marginTop: 14 }}>
                            {prev.attendanceSet === false ? (
                                <span>La jornada {prev.openSession ? prev.openSession.number : ''} está abierta pero
                                    <strong> todavía no marcaste quién vino</strong>. Andá a “Rondas y mesas”.</span>
                            ) : (
                                /* En una liga "nadie repite rival" es aritméticamente imposible. */
                                <span><strong>{prev.players} presentes: {prev.split.sizes.length} mesas de {prev.split.sizes.join(', ')}.</strong>{' '}
                                    En una liga repetir rivales es inevitable — el armado los reparte lo más parejo posible.</span>
                            )}
                        </Aviso>
                    ) : null}

                    {(() => {
                        // Sin cuenta no hay historial (la cédula NO sirve de llave: no prueba identidad).
                        const sinCuenta = this.state.participants.filter((p) => p.status === 'active' && !p.account_id).length;
                        if (!t.event_id || !sinCuenta) return null;
                        return (
                            <Aviso tipo="warn" style={{ marginTop: 14 }}>
                                <strong>{sinCuenta} de {activos.length} no tienen cuenta.</strong> Juegan igual, pero el torneo no
                                les va a quedar en su historial.
                            </Aviso>
                        );
                    })()}

                    {this.state.participants.length ? (
                        <ul className="list-group list-group-flush" style={{ marginTop: 14, maxHeight: 420, overflowY: 'auto' }}>
                            {this.state.participants.map((p) => {
                                const c = contactos[p.id] || {};
                                return (
                                    <li key={p.id} className="list-group-item px-0 py-2 d-flex justify-content-between align-items-center"
                                        style={{ fontSize: 13, opacity: p.status === 'active' ? 1 : 0.55, gap: 8 }}>
                                        <span style={{ minWidth: 0 }}>
                                            <span>{p.display_name}</span>
                                            {p.status === 'active' ? null : (
                                                <span className="text-muted"> · baja{p.dropped_reason ? `: ${MOTIVO_BAJA[p.dropped_reason].toLowerCase()}` : ''}{p.dropped_at_round ? ` (ronda ${p.dropped_at_round})` : ''}</span>
                                            )}
                                            {p.affinity_group ? <span className="badge badge-light ml-2" title="Grupo: se evita sentarlos juntos">{p.affinity_group}</span> : null}
                                            {p.status === 'active' && t.sessions_planned == null && t.status !== 'finished' && (p.present === false || p.present === 0)
                                                ? <span className="badge badge-secondary ml-2">ausente</span> : null}
                                            <div className="text-muted" style={{ fontSize: 11.5 }}>
                                                {c.email || 'sin correo'} · {c.phone || 'sin teléfono'}
                                                {oficial ? (p.consent_at ? ' · informado' : ' · sin informar') : ''}
                                            </div>
                                        </span>
                                        <span className="text-nowrap">
                                            <button type="button" className="btn btn-sm btn-white" onClick={() => this.set({ modalJugador: p })}>Editar</button>
                                            <button type="button" className="btn btn-sm btn-outline-secondary ml-2"
                                                onClick={() => this.cambiarEstadoJugador(p)}>
                                                {p.status === 'active' ? 'Dar de baja' : 'Reactivar'}
                                            </button>
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : null}
                </div>
            </div>
        );
    }

    renderBitacora() {
        const log = this.state.log;
        const fecha = (d) => new Date(d).toLocaleString('es-UY', { timeZone: 'America/Montevideo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
        const resumen = (x) => {
            const d = x.detail || {};
            if (x.action === 'config_changed') return Object.keys(d).join(', ');
            if (x.action === 'tiebreak_set') return (d.order || []).join(' > ');
            if (x.action === 'roll_call') return d.absent && d.absent.length ? `ausentes: ${d.absent.join(', ')}` : 'todos presentes';
            if (x.action === 'late_entry') return `${(d.participants || []).join(', ')} (se perdió ${d.roundsMissed} ronda/s)`;
            if (x.action === 'tier_selected') return `${d.players} jugadores: ${d.format}`;
            if (x.action === 'result_corrected') return `ronda ${d.round}, mesa ${d.table}${d.discarded && d.discarded.length ? ` · se desarmó: ${d.discarded.join(', ')}` : ''}`;
            if (x.action === 'cut_applied') return `${(d.eliminated || []).length} afuera`;
            if (d.event) return d.event;
            if (d.participant) return d.participant;
            if (d.round) return `ronda ${d.round}`;
            if (d.format) return d.format;
            return '';
        };
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom d-flex justify-content-between align-items-center">
                    <h6 className="m-0">Bitácora</h6>
                    <button type="button" className="btn btn-sm btn-white" onClick={this.cargarBitacora}>
                        {log ? 'Actualizar' : 'Ver quién cambió qué'}
                    </button>
                </div>
                {log ? (
                    <div className="card-body" style={{ maxHeight: 320, overflowY: 'auto', fontSize: 12.5 }}>
                        {log.length ? log.map((x) => (
                            <div key={x.id} style={{ borderBottom: '1px solid #F0F2F5', padding: '5px 0' }}>
                                <span className="text-muted">{fecha(x.at)}</span>{' · '}
                                <strong>{ACCIONES_BITACORA[x.action] || x.action}</strong>
                                {resumen(x) ? <span> — {resumen(x)}</span> : null}
                                <span className="text-muted"> · {x.actor || 'sistema'}</span>
                            </div>
                        )) : <span className="text-muted">Todavía no hay nada registrado.</span>}
                    </div>
                ) : null}
            </div>
        );
    }

    renderConfig() {
        const prev = this.state.preview;
        return (
            <div className="row">
                <div className="col-lg-6">
                    {this.renderFormato()}
                    {prev && prev.scoring ? (
                        <div className="card card-small mb-4">
                            <div className="card-header border-bottom"><h6 className="m-0">Cuánto paga cada puesto</h6></div>
                            <div className="card-body p-0 pb-3">
                                <div className="dataTables_wrapper no-footer">
                                    <table className="transaction-history dataTable no-footer" role="grid">
                                        <thead>
                                            <tr role="row">
                                                <th className="text-left">Puesto</th>
                                                {prev.scoring.sizes.map((k) => <th key={k}>Mesa de {k}</th>)}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {prev.scoring.rows.map((row, i) => (
                                                <tr role="row" key={row.position} style={{ background: i % 2 === 0 ? '#ffffff' : '#fcfcfc' }}>
                                                    <td className="text-left">{row.position}°</td>
                                                    {row.points.map((p, j) => (
                                                        <td key={j} style={p == null ? { color: '#D6DBE1' } : null}>
                                                            {p == null ? '—' : Math.round(p * 100) / 100}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div style={{ padding: '10px 16px 0', fontSize: 12.5, color: '#5A6169' }}>
                                    {this.state.tournament.scoring_mode === 'table'
                                        ? 'Puntaje por tabla: un tamaño de mesa sin fila no se puede jugar (la carga lo rechaza).'
                                        : 'Todas las mesas promedian lo mismo: da igual caer en una de 3 o en una de 4.'}
                                </div>
                            </div>
                        </div>
                    ) : null}
                    {this.renderBitacora()}
                </div>
                <div className="col-lg-6">
                    {this.renderEvento()}
                    {this.renderParticipantes()}
                </div>
            </div>
        );
    }

    publicar = () => this.accion(() => api.updateTournament(this.state.id, { published: true }));

    despublicar = () => this.accion(
        () => api.updateTournament(this.state.id, { published: false }),
        { confirmacion: 'El link deja de funcionar para todos los que lo tengan. ¿Seguir?' },
    );

    // Rotar sirve cuando el link se filtró: el viejo deja de andar y sale uno nuevo.
    rotarToken = () => this.accion(
        () => api.updateTournament(this.state.id, { rotateToken: true }),
        { confirmacion: 'Se genera un link nuevo y el anterior deja de funcionar. ¿Seguir?' },
    );

    copiarLink = (url) => {
        // execCommand y no navigator.clipboard: el backoffice se sirve por http en la red del
        // local, y la Clipboard API sólo existe en contextos seguros.
        const ta = document.createElement('textarea');
        ta.value = url; ta.style.position = 'fixed'; ta.style.top = '-1000px';
        document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); window.alert('Link copiado.'); }
        catch (e) { window.prompt('Copiá el link:', url); }
        document.body.removeChild(ta);
    }

    // ───────────────────────────── jornadas (ligas)

    jornadaAbierta = () => this.state.sessions.find((s) => s.status === 'open') || null;

    nuevaJornada = () => {
        const hoy = new Date();
        const iso = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
        const fecha = window.prompt('¿De qué día es la jornada?', iso);
        if (fecha === null) return;
        this.accion(() => api.createSession(this.state.id, fecha || null), { saltarAUltima: true });
    }

    // Guardar la asistencia y armar las mesas son UNA sola acción, porque en el salón son un
    // solo momento: tildás a los que vinieron y repartís. Tenerlas separadas dejaba el botón de
    // "Generar ronda" escondido hasta después de guardar, y un botón que no está no enseña que
    // exista: se lee como que no hay forma de empezar la ronda.
    guardarAsistencia = (armar) => {
        const j = this.jornadaAbierta();
        if (!j) return;
        this.accion(async () => {
            // `presentesActuales()` y NO `state.presentes`: los tildes que se ven en pantalla son
            // un default CALCULADO (todos los activos), y `state.presentes` sigue en null hasta
            // que alguien destilda a alguien. Mandar `state.presentes || []` guardaba CERO
            // presentes justo en el caso más común de una liga —vino todo el mundo, no tocás
            // nada, apretás guardar—, y el error recién aparecía después, al armar las mesas.
            await api.setAttendance(this.state.id, j.id, this.presentesActuales());
            this.set({ presentes: null });
            if (armar) await api.generateRound(this.state.id);
        }, armar ? { saltarAUltima: true } : {});
    }

    cerrarJornada = () => {
        const j = this.jornadaAbierta();
        if (!j) return;
        // Cerrar la jornada estando a mitad de las rondas previstas es un click fácil de dar de
        // más —el botón vive al lado de "Generar ronda"— y termina la noche antes de tiempo.
        const faltan = this.state.tournament.rounds_planned - j.rounds.length;
        const aviso = faltan > 0
            ? `La jornada ${j.number} lleva ${j.rounds.length} de las ${this.state.tournament.rounds_planned} rondas previstas. ¿Cerrarla igual?`
            : null;
        if (aviso && !window.confirm(aviso)) return;
        this.accion(async () => {
            const r = await api.closeSession(this.state.id, j.id);
            if (r && r.ligaTerminada) window.alert('Se cerró la última jornada: la liga terminó y la tabla quedó sellada.');
        });
    }

    // El puesto general sale de la tabla que la pantalla ya tiene cargada: no hace falta que el
    // asiento lo traiga del backend.
    // Antes de que termine alguna mesa todos empatan en cero y el "puesto" sería el orden
    // alfabético: mostrarlo en la tarjeta de mesa hace creer que hay una tabla que no existe.
    rankById = () => {
        const out = {};
        const filas = this.state.standings || [];
        if (!filas.some((r) => r.rounds > 0)) return out;
        filas.forEach((r) => { out[r.participantId] = r.rank; });
        return out;
    }

    rearmarJornada = () => {
        const j = this.jornadaAbierta();
        if (!j) return;
        this.accion(() => api.rebuildSession(this.state.id, j.id), {
            confirmacion: `Se descartan las mesas armadas de la jornada ${j.number} y volvés a marcar quién vino. `
                + 'No se pierde ningún resultado (todavía no hay ninguno cargado). ¿Seguir?',
            saltarAUltima: true,
        });
    }

    ultimaJornada = () => (this.state.sessions.length ? this.state.sessions[this.state.sessions.length - 1] : null);

    reabrirJornada = () => {
        const j = this.ultimaJornada();
        if (!j) return;
        this.accion(() => api.reopenSession(this.state.id, j.id), {
            confirmacion: `Reabrir la jornada ${j.number} para seguir jugándola. Si la liga estaba terminada, se descongela la clasificación. ¿Seguir?`,
        });
    }

    // La selección arranca con TODOS tildados: en una liga viene casi todo el mundo, así que
    // destildar a los tres que faltaron es menos trabajo que tildar a los trece que vinieron.
    presentesActuales = () => {
        if (this.state.presentes) return this.state.presentes;
        const j = this.jornadaAbierta();
        if (j && j.present.length) return j.present;
        return this.state.participants.filter((p) => p.status === 'active').map((p) => p.id);
    }

    togglePresente = (id) => {
        const actual = this.presentesActuales();
        const nuevo = actual.indexOf(id) === -1 ? actual.concat([id]) : actual.filter((x) => x !== id);
        this.set({ presentes: nuevo });
    }

    // Espejo de `splitOptions` del backend (ver utils/tournamentFormats). Se calcula ACÁ y no se
    // toma del preview porque el preview cuenta lo GUARDADO, y lo que hay que mostrar es el reparto
    // de lo que está TILDADO ahora. El backend sigue siendo la autoridad al armar las mesas.
    opcionesDeReparto = (n) => {
        const t = this.state.tournament || {};
        return opcionesDeReparto(n, t.min_table_size, t.table_size);
    }

    renderAsistencia() {
        const j = this.jornadaAbierta();
        if (!j) return null;
        const activos = this.state.participants.filter((p) => p.status === 'active');
        const presentes = this.presentesActuales();
        const conMesas = j.rounds.length > 0;
        const opciones = this.opcionesDeReparto(presentes.length);

        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom d-flex justify-content-between align-items-center">
                    <h6 className="m-0">Jornada {j.number}{j.playedOn ? ` · ${j.playedOn}` : ''} — ¿quiénes vinieron?</h6>
                    <span className="badge badge-warning">{presentes.length} de {activos.length}</span>
                </div>
                <div className="card-body">
                    {conMesas ? (
                        <div style={{ background: '#FFF8E8', borderRadius: '.25rem', padding: '9px 11px', fontSize: 12.5, marginBottom: 12 }}>
                            La jornada ya tiene mesas armadas. Para cambiar quién vino hay que borrar la ronda primero.
                        </div>
                    ) : null}
                    <div className="row">
                        {activos.map((p) => (
                            <div className="col-md-4 col-sm-6" key={p.id}>
                                <label className="d-flex align-items-center" style={{ gap: 7, fontSize: 13, marginBottom: 6, cursor: conMesas ? 'default' : 'pointer' }}>
                                    <input type="checkbox" disabled={conMesas}
                                        checked={presentes.indexOf(p.id) !== -1}
                                        onChange={() => this.togglePresente(p.id)} />
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.display_name}</span>
                                </label>
                            </div>
                        ))}
                    </div>
                    {/* El torneo fija el mínimo y el máximo por mesa; con eso y la gente que vino, las
                        combinaciones posibles son finitas y se listan solas. Todas sientan a TODOS.
                        Cuando hay una sola se muestra igual como texto: desaparecer en silencio se
                        lee como que la pantalla está rota, y encima esconde la única información
                        que importa —cómo van a quedar las mesas. */}
                    {conMesas || !opciones.length ? null : (
                        <div className="d-flex align-items-center flex-wrap" style={{ gap: 10, marginTop: 12 }}>
                            <label className="m-0" style={{ fontSize: 13 }}>Cómo repartir</label>
                            {opciones.length > 1 ? (
                                <select className="form-control form-control-sm" style={{ width: 'auto' }}
                                    value={String(j.tablesWanted || opciones[0].tables)}
                                    disabled={this.state.busy}
                                    onChange={(e) => this.accion(() => api.updateSession(this.state.id, j.id, {
                                        tablesWanted: Number(e.target.value),
                                    }))}>
                                    {opciones.map((o) => (
                                        <option key={o.tables} value={o.tables}>
                                            {o.tables} mesa{o.tables === 1 ? '' : 's'} · {o.sizes.join(' + ')}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <span style={{ fontSize: 13 }}>
                                    <strong>{opciones[0].tables} mesa{opciones[0].tables === 1 ? '' : 's'} · {opciones[0].sizes.join(' + ')}</strong>
                                    <span className="text-muted"> — con {presentes.length} presentes y mesas de {this.state.tournament.min_table_size} a {this.state.tournament.table_size}, es el único reparto posible.</span>
                                </span>
                            )}
                            {(() => {
                                const sel = opciones.find((o) => o.tables === (j.tablesWanted || opciones[0].tables)) || opciones[0];
                                if (sel.relajado) {
                                    return <span className="text-muted" style={{ fontSize: 12.5 }}>
                                        {sel.relajado === 'max'
                                            ? 'Queda alguna mesa de uno más que el máximo, para no armar mesas por debajo del mínimo.'
                                            : 'Queda una mesa por debajo del mínimo, pero juegan todos.'}
                                    </span>;
                                }
                                return opciones.length > 1 ? (
                                    <span className="text-muted" style={{ fontSize: 12.5 }}>
                                        {sel.canAvoid ? 'Con este reparto nadie repite rival.' : 'Con este reparto algunos repiten rival.'}
                                    </span>
                                ) : null;
                            })()}
                        </div>
                    )}

                    {conMesas ? null : (
                        <div className="d-flex align-items-center flex-wrap" style={{ gap: 12, marginTop: 10 }}>
                            <button type="button" className="btn btn-sm btn-accent"
                                disabled={this.state.busy || !presentes.length}
                                onClick={() => this.guardarAsistencia(true)}>
                                Guardar y armar las mesas
                            </button>
                            <button type="button" className="btn btn-sm btn-white"
                                disabled={this.state.busy || !presentes.length}
                                onClick={() => this.guardarAsistencia(false)}>
                                Sólo guardar por ahora
                            </button>
                            <span className="text-muted" style={{ fontSize: 12.5 }}>
                                {presentes.length
                                    ? `Se guarda la asistencia y se arman las mesas.`
                                    : 'Tildá al menos a una persona.'}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ───────────────────────────── pasar lista (torneo de un día)

    // Los tildes arrancan con lo que dice el servidor (`present`): en un torneo de un día casi
    // todos están, así que se destilda a los que faltan. La lista vale para la PRÓXIMA ronda.
    listaActual = () => {
        if (this.state.lista) return this.state.lista;
        return this.state.participants
            .filter((p) => p.status === 'active' && p.present !== false && p.present !== 0)
            .map((p) => p.id);
    }

    toggleLista = (id) => {
        const actual = this.listaActual();
        this.set({ lista: actual.indexOf(id) === -1 ? actual.concat([id]) : actual.filter((x) => x !== id) });
    }

    avisarArmado = (r) => {
        if (!r) return;
        const partes = [];
        if (r.tier) partes.push(`Se fijó el formato del reglamento para ${r.tier.players} jugadores: ${r.tier.format}.`);
        if (r.cutOut && r.cutOut.length) partes.push(`Quedaron afuera por el corte (${r.cutOut.length}): ${r.cutOut.join(', ')}.`);
        if (r.repeats) partes.push(`Esta ronda repite ${r.repeats} cruce${r.repeats === 1 ? '' : 's'} de rivales: no había forma de evitarlo con esta cantidad de gente y mesas.`);
        if (partes.length) window.alert(partes.join('\n\n'));
    }

    generarConLista = (manual = false) => {
        const lista = this.listaActual();
        this.accion(async () => {
            await api.rollCall(this.state.id, lista);
            this.set({ lista: null });
            if (manual) return null;
            return api.generateRound(this.state.id);
        }, {
            saltarAUltima: !manual,
            despues: (r) => (manual ? this.abrirArmadoManual() : this.avisarArmado(r)),
        });
    }

    // Reparto sugerido para arrancar el armado a mano: los presentes en orden de tabla, repartidos
    // con el mismo cálculo que usa el sistema. El juez los mueve desde ahí.
    abrirArmadoManual = () => {
        const t = this.state.tournament;
        const presentes = this.state.participants.filter((p) => p.status === 'active' && p.present !== false && p.present !== 0);
        const orden = (this.state.standings || []).map((r) => r.participantId);
        presentes.sort((a, b) => orden.indexOf(a.id) - orden.indexOf(b.id));
        const [op] = opcionesDeReparto(presentes.length, t.min_table_size, t.table_size);
        const mesas = [];
        let i = 0;
        (op ? op.sizes : [presentes.length]).forEach((k) => {
            mesas.push({ jugadores: presentes.slice(i, i + k).map((p) => ({ id: p.id, name: p.display_name })) });
            i += k;
        });
        const numero = this.state.rounds.filter((r) => r.kind === 'qualifier').length + 1;
        this.set({ modalMesas: { modo: 'manual', mesas, numero } });
    }

    abrirMoverJugadores = () => {
        const rd = this.state.roundData;
        if (!rd) return;
        const mesas = rd.tables.filter((tb) => tb.status !== 'reported').map((tb) => ({
            tableId: tb.id,
            number: tb.number,
            jugadores: tb.seats.map((x) => ({ id: x.participantId, name: x.name })),
        }));
        if (!mesas.length) { window.alert('Todas las mesas de esta ronda ya están cargadas.'); return; }
        this.set({ modalMesas: { modo: 'reseat', mesas, roundId: rd.round.id } });
    }

    guardarMesas = async (mesas) => {
        const m = this.state.modalMesas;
        if (m.modo === 'manual') {
            const r = await api.manualRound(this.state.id, mesas.map((x) => x.jugadores.map((j) => j.id)));
            this.set({ modalMesas: null });
            await this.cargar({ roundView: null });
            this.avisarArmado(r);
        } else {
            await api.reseatRound(this.state.id, m.roundId, mesas.map((x) => ({ tableId: x.tableId, participantIds: x.jugadores.map((j) => j.id) })));
            this.set({ modalMesas: null });
            await this.cargar();
        }
    }

    // Corregir una mesa de una ronda cerrada: primero se pide el impacto (sin escribir nada), se
    // muestra, y recién con el OK se aplica. Devuelve true si se aplicó.
    corregirMesa = async (tableId, results) => {
        const seco = await api.correctTable(this.state.id, tableId, results, false);
        const im = seco.impact || {};
        const lineas = ['Vas a corregir el resultado de esta mesa.'];
        if (im.stage && im.changes) {
            const etapa = im.stage === 'final' ? 'la mesa final' : 'la semifinal';
            lineas.push(`Cambia quién entra a ${etapa}.`);
            if (im.before && im.before.length) lineas.push(`Antes: ${im.before.join(', ')}.`);
            if (im.after && im.after.length) lineas.push(`Ahora: ${im.after.join(', ')}.`);
            if (im.tie) lineas.push(`Además queda un empate en el corte (${im.tie.join(', ')}): lo va a tener que ordenar el juez.`);
            lineas.push(`Se desarma ${im.discards.map((k) => (k === 'final' ? 'la mesa final' : 'la semifinal')).join(' y ')}${im.discardsReported ? ', CON los resultados que ya tenía cargados,' : ''} y hay que volver a armarla.`);
        } else if (im.stage) {
            lineas.push(`No cambia quién está en ${im.stage === 'final' ? 'la mesa final' : 'la semifinal'}.`);
        }
        if (im.reseal) lineas.push('El torneo ya había terminado: la clasificación final se vuelve a sellar con este resultado.');
        lineas.push('Los desempates que haya fijado el juez se anulan si cambia la tabla. ¿Aplicar?');
        if (!window.confirm(lineas.join('\n\n'))) return false;
        await api.correctTable(this.state.id, tableId, results, true);
        await this.cargar();
        return true;
    }

    marcarAsiento = (tableId, seatId, outcome) => this.accion(() => api.setSeatOutcome(this.state.id, tableId, seatId, outcome))

    // Abandono a mitad de partida: se da de baja con motivo y el juez lo carga último en la mesa.
    seFue = (participantId, nombre) => this.accion(
        () => api.updateParticipant(this.state.id, participantId, { status: 'dropped', droppedReason: 'withdrew' }),
        {
            confirmacion: `${nombre} abandonó: queda dado de baja y no juega las próximas rondas. En esta mesa cargalo último, `
                + 'con los PV que tenía. ¿Seguir?',
        },
    )

    renderPasarLista() {
        const t = this.state.tournament;
        const activos = this.state.participants.filter((p) => p.status === 'active');
        const lista = this.listaActual();
        const numero = this.state.rounds.filter((r) => r.kind === 'qualifier').length + 1;
        const cut = etapasDe(t, lista.length).find((x) => x.type === 'cut');
        const conCorte = cut && numero > Number(t.rounds_planned);
        // Con el corte vigente, la ronda se arma sólo con los que lo pasaron: el reparto que se
        // muestra tiene que ser el de ellos, no el de todos los presentes.
        const victorias = {};
        (this.state.standings || []).forEach((r) => { victorias[r.participantId] = r.wins; });
        const juegan = conCorte ? lista.filter((id) => (victorias[id] || 0) >= cut.min) : lista;
        const [op] = this.opcionesDeReparto(juegan.length);
        const tabla = parseJson(t.score_table) || {};
        const sinFila = t.scoring_mode === 'table' && op ? [...new Set(op.sizes)].filter((k) => !Array.isArray(tabla[k]) || tabla[k].length < k) : [];
        const plan = parseJson(t.bracket_plan);
        const formato = numero === 1 && plan && plan.tiers
            ? describirEtapas(t.rounds_planned, etapasDe(t, lista.length)) : null;
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom d-flex justify-content-between align-items-center">
                    <h6 className="m-0">Ronda {numero} — pasar lista</h6>
                    <span className="badge badge-warning">{lista.length} de {activos.length} presentes</span>
                </div>
                <div className="card-body">
                    <div className="text-muted" style={{ fontSize: 12.5, marginBottom: 10 }}>
                        Destildá a los que no están. Un ausente no se sienta: si se lo sentara, cobraría el último puesto y su
                        mesa jugaría de a tres.
                    </div>
                    <div className="row">
                        {activos.map((p) => (
                            <div className="col-md-4 col-sm-6" key={p.id}>
                                <label className="d-flex align-items-center" style={{ gap: 7, fontSize: 13, marginBottom: 6, cursor: 'pointer' }}>
                                    <input type="checkbox" checked={lista.indexOf(p.id) !== -1} onChange={() => this.toggleLista(p.id)} />
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.display_name}</span>
                                </label>
                            </div>
                        ))}
                    </div>
                    {op ? (
                        <Aviso tipo={op.canAvoid && !op.relajado ? 'info' : 'warn'} style={{ marginTop: 10 }}>
                            <strong>{juegan.length} {conCorte ? 'juegan' : 'presentes'}: {op.tables} mesas de {op.sizes.join(' + ')}.</strong>{' '}
                            {op.relajado === 'max' ? 'Alguna mesa queda de uno más que el máximo, para no armar mesas chicas. ' : null}
                            {op.relajado === 'min' ? 'Queda una mesa por debajo del mínimo, pero juegan todos. ' : null}
                            {numero === 1 ? (op.canAvoid
                                ? 'Alcanza para que nadie repita rival.'
                                : `Van a repetirse rivales: hacen falta al menos ${Math.max(...op.sizes)} mesas y hay ${op.tables}.`) : null}
                            {conCorte ? ` El corte deja afuera a ${lista.length - juegan.length} (menos de ${cut.min} victoria).` : null}
                        </Aviso>
                    ) : null}
                    {(() => {
                        // El cartel accionable: con esta cantidad y este tamaño de mesa se repiten rivales,
                        // pero con mesas más chicas se evita del todo. Es el único momento en que el
                        // organizador todavía lo puede arreglar con un click (en Configuración).
                        const prev = this.state.preview;
                        if (numero !== 1 || !prev || !prev.split || prev.split.canAvoid || !prev.split.suggestedOption) return null;
                        if (prev.players !== lista.length) return null;
                        return (
                            <Aviso tipo="warn">
                                Con <strong>{prev.split.suggestedOption.tables} mesas de {prev.split.suggestedOption.sizes.join(' + ')}</strong> nadie
                                repite rival. Para eso cambiá “Jugadores por mesa” y “Mínimo por mesa” en Configuración antes de generar.
                            </Aviso>
                        );
                    })()}
                    {sinFila.length ? (
                        <Aviso tipo="error">
                            Con esta cantidad se arma una mesa de {sinFila.join(', ')} y la tabla de puntos no tiene esa fila.
                            Cargala en Configuración o cambiá el tamaño de mesa.
                        </Aviso>
                    ) : null}
                    {formato ? (
                        <Aviso tipo="info">Con {lista.length} jugadores el reglamento pide: <strong>{formato}</strong>. Se fija al generar esta ronda.</Aviso>
                    ) : null}
                    {this.state.declared && numero === 1 && lista.length < 12 ? (
                        <Aviso tipo="warn">El mínimo oficial del clasificatorio es 12 jugadores.</Aviso>
                    ) : null}
                    <div className="d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
                        <button type="button" className="btn btn-sm btn-accent" disabled={this.state.busy || lista.length < 2 || sinFila.length > 0}
                            onClick={() => this.generarConLista(false)}>
                            Guardar lista y generar ronda {numero}
                        </button>
                        <button type="button" className="btn btn-sm btn-white" disabled={this.state.busy || lista.length < 2}
                            onClick={() => this.generarConLista(true)}>
                            Armar a mano
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ───────────────────────────── rondas

    // En una liga la navegación plana no sirve: 16 rondas son 16 botones sin contexto, y la
    // gente no se ubica por "ronda 11" sino por "el martes pasado". Se agrupa por jornada.
    renderNavJornadas() {
        return (
            <div className="d-flex flex-wrap" style={{ gap: 12 }}>
                {this.state.sessions.map((j) => (
                    <div key={j.id} style={{
                        border: '1px solid ' + (j.status === 'open' ? '#FFB400' : '#E1E5EB'),
                        borderRadius: '.25rem', padding: '6px 9px',
                    }}>
                        <div style={{ fontSize: 11, color: '#818EA3', marginBottom: 4 }}>
                            Jornada {j.number}{j.playedOn ? ` · ${j.playedOn}` : ''}
                            {j.status === 'open' ? ' · abierta' : ''}
                        </div>
                        <div className="d-flex" style={{ gap: 5 }}>
                            {j.rounds.length ? j.rounds.map((r) => (
                                <button type="button" key={r.id}
                                    className={'btn btn-sm ' + (r.id === this.state.roundView ? 'btn-accent' : 'btn-white')}
                                    onClick={() => this.cargarRonda(r.id)}>
                                    R{r.number}{r.status === 'closed' ? ' ✓' : ''}
                                </button>
                            )) : <span className="text-muted" style={{ fontSize: 12 }}>sin rondas</span>}
                        </div>
                    </div>
                ))}
                {this.state.sessions.length ? null : (
                    <span className="text-muted" style={{ fontSize: 13 }}>Todavía no empezó ninguna jornada.</span>
                )}
            </div>
        );
    }

    renderRondas() {
        const t = this.state.tournament;
        const rd = this.state.roundData;
        const ronda = this.state.rounds.find((r) => r.id === this.state.roundView);
        const clasif = this.state.rounds.filter((r) => r.kind === 'qualifier');
        const clasifCerradas = clasif.every((r) => r.status === 'closed');
        const hayFinal = this.state.rounds.some((r) => r.kind === 'final');
        const semi = this.state.rounds.find((r) => r.kind === 'semifinal') || null;
        const hayEtapa = this.state.rounds.some((r) => r.kind !== 'qualifier');
        const presentesHoy = this.listaActual().length;
        const totalPrelim = totalPreliminares(t, presentesHoy);
        const prelimCompletas = clasif.length >= totalPrelim && clasifCerradas && clasif.length > 0;
        const esLiga = t.sessions_planned != null;
        const jornada = this.jornadaAbierta();
        const rondasDeLaJornada = jornada ? jornada.rounds.length : 0;
        const todoCerrado = this.state.rounds.every((r) => r.status === 'closed');

        const puedeGenerar = esLiga
            // En liga: jornada abierta, con presentes marcados y cupo de rondas. Y NO mientras se
            // está mostrando la tarjeta de asistencia (jornada sin rondas): ahí la acción es
            // "Guardar y armar las mesas", que incluye los tildes que todavía no se guardaron.
            // Con los dos botones a la vez, tildar al que llegó tarde y apretar "Generar ronda"
            // armaba las mesas con la lista del servidor y descartaba la selección en silencio.
            ? !!jornada && jornada.present.length > 0 && rondasDeLaJornada > 0
              && rondasDeLaJornada < t.rounds_planned && todoCerrado
            : clasif.length < totalPrelim && !hayEtapa && todoCerrado && t.status !== 'finished';
        const puedeNuevaJornada = esLiga && !jornada && t.status !== 'finished'
            && this.state.sessions.length < t.sessions_planned;
        const puedeCerrarJornada = esLiga && !!jornada && rondasDeLaJornada > 0 && todoCerrado;
        const nadaCargado = !!rd && rd.tables.every((tb) => tb.status !== 'reported');
        // Rearmar borra TODAS las rondas de la jornada, así que la condición mira TODA la jornada.
        // Mirando sólo la ronda en pantalla, en una liga de 2 rondas por martes el botón aparecía
        // sobre la R2 recién generada con la R1 ya jugada: el backend lo rechazaba, pero el cartel
        // afirmaba "no se pierde ningún resultado (todavía no hay ninguno cargado)", que era falso.
        const jornadaSinResultados = !!jornada && jornada.rounds.every((r) => !r.reported);
        const puedeRearmar = esLiga && !!jornada && rondasDeLaJornada > 0 && jornadaSinResultados;
        // Se mira la ronda EN PANTALLA, que acá es la final misma: no repito el error de `rearmar`,
        // que preguntaba por una ronda para decidir sobre toda la jornada.
        // Con una jornada abierta y sin mesas, la pantalla es la de ARMAR esa jornada: mostrar
        // abajo la ronda del martes pasado no aporta nada y confunde sobre qué se está creando.
        const mostrandoOtraJornada = esLiga && !!jornada && rondasDeLaJornada === 0
            && !!ronda && !jornada.rounds.some((r) => r.id === ronda.id);
        const esUltima = !!ronda && this.state.rounds.length && this.state.rounds[this.state.rounds.length - 1].id === ronda.id;
        // Con una etapa armada después, "Reabrir" ya no es el camino (el backend lo rechaza): la
        // corrección va por "Corregir" en la mesa, que muestra el impacto.
        const hayPosterior = !esLiga && !!ronda && this.state.rounds.some((r) => r.number > ronda.number && r.kind !== 'qualifier');
        const puedeDesarmarFinal = !esLiga && !!ronda && ronda.kind !== 'qualifier' && nadaCargado && esUltima;
        const puedeRearmarRonda = !esLiga && !!ronda && ronda.kind === 'qualifier' && ronda.status !== 'closed' && nadaCargado && esUltima;
        const puedeMover = !!ronda && ronda.status !== 'closed' && !!rd && rd.tables.some((tb) => tb.status !== 'reported') && !mostrandoOtraJornada;
        const puedeSemi = !esLiga && tieneEtapa(t, 'semifinal', presentesHoy) && !semi && !hayFinal && prelimCompletas;
        const ultima = this.ultimaJornada();
        const puedeReabrirJornada = esLiga && !jornada && !!ultima && !hayFinal;
        const puedeFinal = !esLiga && tieneEtapa(t, 'final', presentesHoy) && !hayFinal && prelimCompletas
            && (!tieneEtapa(t, 'semifinal', presentesHoy) || (!!semi && semi.status === 'closed'));
        // Cerrar la última preliminar SIN etapa final termina el torneo y corona al primero de la
        // tabla. Era la trampa del alta sin mesa final: ahora se pregunta antes.
        const cierraYTermina = !esLiga && !!ronda && ronda.kind === 'qualifier' && !tieneEtapa(t, 'final', presentesHoy)
            && clasif.length >= totalPrelim && esUltima;
        const cargadas = rd ? rd.tables.filter((x) => x.status === 'reported').length : 0;

        return (
            <div>
                <div className="card card-small mb-4">
                    <div className="card-body d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 12 }}>
                        {esLiga ? this.renderNavJornadas() : (
                        <div className="d-flex flex-wrap" style={{ gap: 7 }}>
                            {this.state.rounds.map((r) => (
                                <button type="button" key={r.id}
                                    className={'btn btn-sm ' + (r.id === this.state.roundView ? 'btn-accent' : 'btn-white')}
                                    onClick={() => this.cargarRonda(r.id)}>
                                    {r.kind === 'qualifier' ? 'Ronda ' + r.number : (r.stage_label || (r.kind === 'final' ? 'Mesa final' : 'Semifinal'))}
                                    {r.status === 'closed' ? ' ✓' : ''}
                                </button>
                            ))}
                            {this.state.rounds.length ? null : <span className="text-muted" style={{ fontSize: 13 }}>Todavía no hay rondas.</span>}
                        </div>
                        )}
                        <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                            {/* El estado y el conteo son DE LA RONDA que se está mirando. Con una jornada
                                nueva abierta, la ronda en memoria es la del martes pasado: mostrar
                                "Cerrada · 4 de 4 mesas cargadas" al lado de la asistencia de HOY hace
                                creer que esas mesas son de esta jornada. */}
                            {ronda && !mostrandoOtraJornada ? (
                                <span className={'badge ' + (ESTADO_RONDA[ronda.status] || {}).badge}>
                                    {(ESTADO_RONDA[ronda.status] || {}).label}
                                </span>
                            ) : null}
                            {rd && !mostrandoOtraJornada ? <span className="text-muted" style={{ fontSize: 13 }}>{cargadas} de {rd.tables.length} mesas cargadas</span> : null}
                            {puedeDesarmarFinal ? (
                                <button type="button" className="btn btn-sm btn-white" disabled={this.state.busy}
                                    onClick={() => this.accion(() => api.discardFinal(this.state.id), {
                                        confirmacion: `Se descarta ${ronda.kind === 'final' ? 'la mesa final' : 'la semifinal'} y se puede volver a armar. ¿Seguir?`,
                                        saltarAUltima: true,
                                    })}>
                                    Desarmar {ronda.kind === 'final' ? 'mesa final' : 'semifinal'}
                                </button>
                            ) : null}
                            {puedeRearmarRonda ? (
                                <button type="button" className="btn btn-sm btn-white" disabled={this.state.busy}
                                    title="Descarta las mesas de esta ronda y las vuelve a sortear con la lista actual. Sirve si la lista estaba mal."
                                    onClick={() => this.accion(() => api.rebuildRound(this.state.id, ronda.id), {
                                        confirmacion: 'Se descartan las mesas de esta ronda y se vuelven a armar con los presentes de ahora. '
                                            + 'No se pierde ningún resultado (todavía no hay ninguno cargado). ¿Seguir?',
                                        saltarAUltima: true,
                                        despues: this.avisarArmado,
                                    })}>
                                    Rearmar ronda
                                </button>
                            ) : null}
                            {puedeRearmarRonda ? (
                                <button type="button" className="btn btn-sm btn-white" disabled={this.state.busy}
                                    onClick={this.abrirArmadoManual}>
                                    Armar a mano
                                </button>
                            ) : null}
                            {puedeMover ? (
                                <button type="button" className="btn btn-sm btn-white" disabled={this.state.busy}
                                    title="Mover jugadores entre mesas o cambiar el orden de juego, antes de cargar"
                                    onClick={this.abrirMoverJugadores}>
                                    Mover jugadores
                                </button>
                            ) : null}
                            {puedeRearmar ? (
                                <button type="button" className="btn btn-sm btn-white" disabled={this.state.busy}
                                    onClick={this.rearmarJornada}
                                    title="Descarta las mesas y vuelve a marcar quién vino. Sirve cuando llega alguien tarde.">
                                    Rearmar jornada
                                </button>
                            ) : null}
                            {puedeReabrirJornada ? (
                                <button type="button" className="btn btn-sm btn-white" disabled={this.state.busy}
                                    onClick={this.reabrirJornada}>
                                    Reabrir jornada {ultima.number}
                                </button>
                            ) : null}
                            {puedeNuevaJornada ? (
                                <button type="button" className="btn btn-sm btn-accent" disabled={this.state.busy}
                                    onClick={this.nuevaJornada}>
                                    Nueva jornada
                                </button>
                            ) : null}
                            {puedeCerrarJornada ? (
                                <button type="button" className="btn btn-sm btn-white" disabled={this.state.busy}
                                    onClick={this.cerrarJornada}>
                                    Cerrar jornada {jornada.number}
                                </button>
                            ) : null}
                            {puedeGenerar && esLiga ? (
                                <button type="button" className="btn btn-sm btn-accent" disabled={this.state.busy}
                                    onClick={() => this.accion(() => api.generateRound(this.state.id), { saltarAUltima: true, despues: this.avisarArmado })}>
                                    Generar ronda
                                </button>
                            ) : null}
                            {puedeSemi ? (
                                <button type="button" className="btn btn-sm btn-accent" disabled={this.state.busy}
                                    onClick={() => this.accion(() => api.generateSemifinal(this.state.id), { saltarAUltima: true })}>
                                    Armar semifinal
                                </button>
                            ) : null}
                            {puedeFinal ? (
                                <button type="button" className="btn btn-sm btn-accent" disabled={this.state.busy}
                                    onClick={() => this.accion(() => api.generateFinal(this.state.id), { saltarAUltima: true })}>
                                    Armar mesa final
                                </button>
                            ) : null}
                            {ronda && ronda.status !== 'closed' && !mostrandoOtraJornada ? (
                                <button type="button" className="btn btn-sm btn-white"
                                    disabled={this.state.busy || !rd || cargadas < rd.tables.length}
                                    title={rd && cargadas < rd.tables.length ? `Faltan ${rd.tables.length - cargadas} mesa(s) por cargar` : ''}
                                    onClick={() => this.accion(() => api.updateRound(this.state.id, ronda.id, { status: 'closed' }), cierraYTermina ? {
                                        confirmacion: 'Este torneo NO tiene mesa final: al cerrar esta ronda termina y gana el primero de la tabla. '
                                            + 'Si tiene que haber final, cancelá y elegila en Configuración. ¿Cerrar y terminar el torneo?',
                                    } : {})}>
                                    {cierraYTermina ? 'Cerrar ronda y terminar' : 'Cerrar ronda'}
                                </button>
                            ) : null}
                            {ronda && ronda.status === 'closed' && !mostrandoOtraJornada && !hayPosterior ? (
                                <button type="button" className="btn btn-sm btn-white" disabled={this.state.busy}
                                    onClick={() => this.accion(
                                        () => api.updateRound(this.state.id, ronda.id, { status: 'open' }),
                                        { confirmacion: 'Reabrir la ronda descongela la clasificación final. ¿Seguir?' },
                                    )}>
                                    Reabrir
                                </button>
                            ) : null}
                        </div>
                    </div>
                </div>

                {esLiga && jornada && rondasDeLaJornada === 0 ? this.renderAsistencia() : null}
                {!esLiga && puedeGenerar ? this.renderPasarLista() : null}
                {!esLiga && (puedeSemi || puedeFinal) && this.state.ties.some((g) => !g.resolved) ? (
                    <Aviso tipo="warn">
                        Hay jugadores empatados en toda la cadena de desempate. Si el empate cae en el corte, la etapa no se
                        arma hasta que el juez lo ordene: mirá la pestaña Posiciones.
                    </Aviso>
                ) : null}

                {/* Sin este encabezado la pantalla mostraba mesas cargadas y con puntajes sin decir de
                    cuándo eran: con una jornada nueva abierta, lo que se veía abajo era el martes
                    PASADO y se leía como "las mesas de hoy ya están hechas". */}
                {rd && ronda && !mostrandoOtraJornada ? (() => {
                    const jr = this.state.sessions.find((x) => x.rounds.some((r) => r.id === ronda.id));
                    const deOtraJornada = false;
                    return (
                        <div className="d-flex align-items-center flex-wrap mb-3" style={{ gap: 10 }}>
                            <h6 className="m-0">
                                {ronda.kind === 'qualifier' ? `Ronda ${ronda.number}` : (ronda.stage_label || (ronda.kind === 'final' ? 'Mesa final' : 'Semifinal'))}
                                {jr ? ` · Jornada ${jr.number}${jr.playedOn ? ` · ${jr.playedOn}` : ''}` : ''}
                            </h6>
                            <span className={'badge ' + (ronda.status === 'closed' ? 'badge-success' : 'badge-warning')}>
                                {ronda.status === 'closed' ? 'cerrada' : 'en juego'}
                            </span>
                            {deOtraJornada ? (
                                <span className="text-muted" style={{ fontSize: 12.5 }}>
                                    Estás viendo una jornada anterior. La jornada {jornada.number} todavía no tiene mesas.
                                </span>
                            ) : null}
                        </div>
                    );
                })() : null}

                {rd && !mostrandoOtraJornada ? (
                    <div className="row">
                        {rd.tables.map((table) => (
                            <div className="col-lg-4 col-md-6" key={table.id}>
                                <TournamentTableCard
                                    table={table}
                                    usesRawScore={!!t.uses_raw_score}
                                    rawRequired={!!t.uses_raw_score && this.state.declared
                                        && ronda && ronda.kind === 'qualifier'
                                        && this.state.chain.some((c) => c.key === 'raw' || c.key === 'raw_share')}
                                    rankById={this.rankById()}
                                    readOnly={!ronda}
                                    roundClosed={!!ronda && ronda.status === 'closed'}
                                    onReport={async (tableId, results) => {
                                        await api.reportTable(this.state.id, tableId, results);
                                        await this.cargar();
                                    }}
                                    onCorrect={this.corregirMesa}
                                    onClear={(tableId) => this.accion(() => api.clearTable(this.state.id, tableId))}
                                    onSeatOutcome={ronda && ronda.status !== 'closed' ? this.marcarAsiento : null}
                                    onWithdraw={ronda && ronda.status !== 'closed' && ronda.kind === 'qualifier' ? this.seFue : null}
                                />
                            </div>
                        ))}
                    </div>
                ) : null}

                {/* El que queda libre no está en ninguna mesa, así que sin esto la ronda se ve
                    con menos gente que la que vino y no se entiende por qué. */}
                {rd && rd.byes && rd.byes.length ? (
                    <div style={{
                        background: '#F5F6F8', border: '1px solid #E1E5EB', borderRadius: '.25rem',
                        padding: '9px 12px', fontSize: 13, marginBottom: 14,
                    }}>
                        <strong>Libre esta ronda:</strong>{' '}
                        {rd.byes.map((b) => `${b.name}${b.points != null ? ` (+${b.points})` : ''}`).join(', ')}
                        <span className="text-muted"> — se lleva el promedio y le cuenta como ronda jugada.</span>
                    </div>
                ) : null}

                {rd && !mostrandoOtraJornada ? (
                    <p className="text-muted" style={{ fontSize: 12.5, lineHeight: 1.55 }}>
                        Se carga tocando los nombres <strong>en orden de llegada</strong>; el último se completa solo.
                        El <span style={{
                            fontSize: 10.5, color: '#A7AEB8', background: '#F5F6F8',
                            border: '1px solid #EDEFF2', borderRadius: 10, padding: '0 6px',
                        }}>#</span> gris al lado del nombre es el puesto en la <strong>tabla general</strong>, no en esta mesa.
                        <strong> Empataron</strong> agrupa al siguiente con el anterior
                        {this.state.declared ? ' — cada empatado suma ese puesto (con 1·2·2·4 los dos suman un 2º).'
                            : ' — los empatados reparten el promedio de los lugares que ocupan, así el total de la mesa no cambia.'}
                        {' '}Una mesa de una ronda cerrada se corrige con <strong>Corregir</strong>: antes de aplicar te muestra qué cambia.
                    </p>
                ) : null}
            </div>
        );
    }

    // ───────────────────────────── posiciones

    tablaPosiciones(filas, titulo, columnas) {
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom"><h6 className="m-0">{titulo}</h6></div>
                <div className="card-body p-0 pb-3">
                    <div className="dataTables_wrapper no-footer">
                        <table className="transaction-history dataTable no-footer" role="grid">
                            <thead><tr role="row">{columnas.map((c) => <th key={c.k} className={c.left ? 'text-left' : ''}>{c.t}</th>)}</tr></thead>
                            <tbody>
                                {filas.length ? filas.map((f, i) => (
                                    <tr role="row" key={i} style={{ background: i % 2 === 0 ? '#ffffff' : '#fcfcfc' }}>
                                        {columnas.map((c) => <td key={c.k} className={c.left ? 'text-left' : ''}>{c.render(f, i)}</td>)}
                                    </tr>
                                )) : (
                                    <tr><td colSpan={columnas.length} className="text-center" style={{ padding: 26, color: '#818EA3' }}>Sin datos todavía.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    // El QR se arma en el navegador con la librería `qrcode`: el link no pasa por ningún servicio
    // externo (llevaría el token público a un tercero).
    generarQr = async (url) => {
        if (this.state.qr && this.state.qr.url === url) return;
        try {
            const dataUrl = await QRCode.toDataURL(url, { margin: 1, width: 480 });
            this.set({ qr: { url, dataUrl } });
        } catch (e) { this.set({ qr: null }); }
    }

    imprimirQr = () => {
        const t = this.state.tournament;
        const qr = this.state.qr;
        if (!qr) return;
        const w = window.open('', '_blank');
        if (!w) { window.alert('El navegador bloqueó la ventana. Permití ventanas emergentes para imprimir el QR.'); return; }
        const titulo = String(t.title || 'Torneo').replace(/[<>&"]/g, '');
        w.document.write(`<!doctype html><html><head><title>${titulo}</title></head><body style="font-family:sans-serif;text-align:center;padding:40px">`
            + `<h1 style="font-size:28px;margin:0 0 8px">${titulo}</h1><p style="font-size:18px;margin:0 0 24px">Escaneá para ver tu mesa y la tabla en vivo</p>`
            + `<img src="${qr.dataUrl}" style="width:420px;height:420px" alt="QR"/><p style="font-size:13px;color:#555;margin-top:18px">${qr.url}</p>`
            + '<script>window.onload=function(){window.print()}</script></body></html>');
        w.document.close();
    }

    // Dónde está este torneo en la cartelera del televisor: los bloques que lo nombran y los
    // automáticos ("el que se esté jugando ahora"), en todas las pantallas. Antes el botón creaba
    // el bloque a ciegas —sin decir en qué pantalla ni dejar ir a verlo— y desde acá no había
    // forma de saber si ya estaba.
    cargarTv = async () => {
        if (this.tvCargando) return;
        this.tvCargando = true;
        try {
            const { screens } = await getScreens();
            const lista = screens && screens.length ? screens : ['principal'];
            const porPantalla = await Promise.all(lista.map((sc) => getBlocks(sc).then((r) => ({ sc, blocks: (r && r.blocks) || [] }))));
            const bloques = [];
            porPantalla.forEach(({ sc, blocks }) => blocks
                .filter((b) => b.kind === 'tournament' && (Number(b.tournamentId) === Number(this.state.id) || b.tournamentId == null))
                .forEach((b) => bloques.push({ screen: sc, block: b })));
            this.set({ tv: { screens: lista, bloques } });
        } catch (e) {
            this.set({ tv: { screens: ['principal'], bloques: [], error: e.message } });
        } finally {
            this.tvCargando = false;
        }
    }

    agregarATv = () => this.accion(async () => {
        const tv = this.state.tv || { screens: ['principal'] };
        const screen = this.state.tvPantalla || tv.screens[0] || 'principal';
        await createBlock({ screen, kind: 'tournament', tournamentId: Number(this.state.id), durationSeconds: 20, itemLimit: 0 });
        await this.cargarTv();
    })

    // Con el bloque en la URL, la cartelera abre ESE bloque (lo lleva a la vista y lo marca) en
    // vez del principio de una lista donde hay que buscarlo.
    irACartelera = (screen, blockId) => {
        const params = new URLSearchParams();
        if (screen && screen !== 'principal') params.set('pantalla', screen);
        if (blockId) params.set('bloque', String(blockId));
        const qs = params.toString();
        navigateTo(`/cartelera${qs ? `?${qs}` : ''}`);
    }

    renderTv() {
        const tv = this.state.tv;
        if (!tv) { this.cargarTv(); return <div className="text-muted" style={{ fontSize: 12.5 }}>Mirando la cartelera…</div>; }
        const t = this.state.tournament;
        const dur = (b) => `${b.durationSeconds} s`;
        // Cuántas posiciones muestra: es lo que más se ajusta de un bloque de torneo, y sin
        // decirlo acá había que ir a la cartelera a averiguarlo.
        const posiciones = (b) => (b.itemLimit === 0 ? 'todas las posiciones'
            : `${b.itemLimit == null ? tipoDe('tournament').itemsDefault : b.itemLimit} posiciones`);
        const propios = tv.bloques.filter((x) => x.block.tournamentId != null);
        const auto = tv.bloques.filter((x) => x.block.tournamentId == null);
        const enJuego = t.status === 'running' || t.status === 'final';
        return (
            <div style={{ borderTop: '1px solid #EDEFF2', marginTop: 14, paddingTop: 12 }}>
                <label className="d-block" style={{ fontWeight: 500 }}>Televisor del local</label>
                {propios.map(({ screen, block }) => (
                    <div key={block.id} className="d-flex align-items-center flex-wrap" style={{ gap: 8, fontSize: 13, marginBottom: 6 }}>
                        <span className={'badge ' + (block.isActive === false ? 'badge-secondary' : 'badge-success')}>
                            {block.isActive === false ? 'apagado' : 'en la cartelera'}
                        </span>
                        <span>Pantalla <strong>{screen}</strong> · {dur(block)} por vuelta · {posiciones(block)}</span>
                        <button type="button" className="btn btn-sm btn-link p-0" onClick={() => this.irACartelera(screen, block.id)}>
                            Configurar en Cartelera TV
                        </button>
                    </div>
                ))}
                {!propios.length && auto.map(({ screen, block }) => (
                    <div key={block.id} className="d-flex align-items-center flex-wrap" style={{ gap: 8, fontSize: 13, marginBottom: 6 }}>
                        <span className={'badge ' + (enJuego && block.isActive !== false ? 'badge-success' : 'badge-secondary')}>
                            {enJuego && block.isActive !== false ? 'se muestra solo' : 'no se muestra'}
                        </span>
                        <span>
                            La pantalla <strong>{screen}</strong> tiene un bloque de "el torneo que se esté jugando":
                            {enJuego ? ' muestra éste mientras esté publicado y en juego' : ' este torneo no está en juego, así que no aparece'}
                            {' '}(si hay dos torneos en juego, muestra el más nuevo).
                        </span>
                        <button type="button" className="btn btn-sm btn-link p-0" onClick={() => this.irACartelera(screen, block.id)}>
                            Configurar en Cartelera TV
                        </button>
                    </div>
                ))}
                {propios.length ? null : (
                    <div className="d-flex align-items-center flex-wrap" style={{ gap: 8, marginTop: 4 }}>
                        {tv.screens.length > 1 ? (
                            <select className="form-control form-control-sm" style={{ width: 'auto' }} aria-label="Pantalla del televisor"
                                value={this.state.tvPantalla || tv.screens[0]}
                                onChange={(e) => this.set({ tvPantalla: e.target.value })}>
                                {tv.screens.map((sc) => <option key={sc} value={sc}>Pantalla {sc}</option>)}
                            </select>
                        ) : null}
                        <button type="button" className="btn btn-sm btn-white" disabled={this.state.busy} onClick={this.agregarATv}>
                            Agregar a la TV{tv.screens.length > 1 ? '' : ` (pantalla ${tv.screens[0]})`}
                        </button>
                        <span className="text-muted" style={{ fontSize: 12 }}>
                            Crea un bloque "Tabla de torneo" de 20 s con este torneo; después se ajusta en Cartelera TV.
                        </span>
                    </div>
                )}
                {tv.error ? <div className="text-muted" style={{ fontSize: 12 }}>No se pudo leer la cartelera: {tv.error}</div> : null}
            </div>
        );
    }

    renderTablaPublica() {
        const t = this.state.tournament;
        const token = t.public_token;
        if (token) this.generarQr(urlPublica(token));
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom"><h6 className="m-0">Tabla pública, QR y televisor</h6></div>
                <div className="card-body">
                    {token ? (
                        <div className="row">
                            <div className="col-md-8">
                                <div className="form-group">
                                    <label>Link para compartir con los jugadores</label>
                                    <input type="text" className="form-control" readOnly value={urlPublica(token)}
                                        onFocus={(e) => e.target.select()} />
                                </div>
                                <div className="d-flex" style={{ gap: 7, flexWrap: 'wrap' }}>
                                    <button type="button" className="btn btn-sm btn-accent"
                                        onClick={() => this.copiarLink(urlPublica(token))}>Copiar link</button>
                                    <a className="btn btn-sm btn-white" href={urlPublica(token)}
                                        target="_blank" rel="noopener noreferrer">Abrir</a>
                                    <button type="button" className="btn btn-sm btn-white" disabled={!this.state.qr}
                                        onClick={this.imprimirQr}>Imprimir QR</button>
                                    <button type="button" className="btn btn-sm btn-white" disabled={this.state.busy}
                                        onClick={this.rotarToken}>Cambiar el link</button>
                                    <button type="button" className="btn btn-sm btn-outline-secondary" disabled={this.state.busy}
                                        onClick={this.despublicar}>Despublicar</button>
                                </div>
                                <div className="text-muted" style={{ fontSize: 12.5, marginTop: 9, lineHeight: 1.45 }}>
                                    Cualquiera con el link ve las mesas de la ronda, el orden de juego y la tabla con los nombres. No
                                    muestra correos ni teléfonos. Si se filtró, "Cambiar el link" deja el anterior sin efecto (hay que
                                    reimprimir el QR).
                                </div>
                                {this.renderTv()}
                            </div>
                            <div className="col-md-4 text-center">
                                {this.state.qr ? <img src={this.state.qr.dataUrl} alt="QR de la tabla pública" style={{ width: 160, height: 160 }} /> : null}
                            </div>
                        </div>
                    ) : (
                        <div>
                            <button type="button" className="btn btn-sm btn-accent" disabled={this.state.busy}
                                onClick={this.publicar}>Publicar la tabla</button>
                            <div className="text-muted" style={{ fontSize: 12.5, marginTop: 9, lineHeight: 1.45 }}>
                                Genera un link y un QR para que los jugadores vean su mesa y cómo van desde el celular, y habilita
                                mostrarlo en el televisor del local. Se puede despublicar en cualquier momento.
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ───────────────────────────── planilla para Devir

    revisarPlanilla = async () => {
        try {
            this.set({ exportCheck: await api.exportCheck(this.state.id) });
        } catch (e) { window.alert(e.message); }
    }

    bajarPlanilla = async (formato) => {
        this.set({ busy: true });
        try {
            await api.downloadExport(this.state.id, formato);
        } catch (e) {
            window.alert(e.message);
        } finally {
            this.set({ busy: false });
            this.revisarPlanilla();
            if (this.state.log) this.cargarBitacora();
        }
    }

    renderPlanilla() {
        const t = this.state.tournament;
        const chk = this.state.exportCheck;
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom d-flex justify-content-between align-items-center">
                    <h6 className="m-0">Planilla para Devir</h6>
                    <button type="button" className="btn btn-sm btn-white" onClick={this.revisarPlanilla}>
                        {chk ? 'Revisar de nuevo' : 'Revisar datos'}
                    </button>
                </div>
                <div className="card-body">
                    <div className="text-muted" style={{ fontSize: 12.5, marginBottom: 10, lineHeight: 1.45 }}>
                        Las bases piden mandarla a <strong>op-juegosdemesa@devir.com</strong> dentro de la semana del evento,
                        con nombre, correo y teléfono del ganador, nombre y correo del resto, y las fotos. El sistema arma la
                        planilla; el mail y las fotos se mandan a mano.
                    </div>
                    {t.status !== 'finished' ? (
                        <Aviso tipo="info">La planilla sale cuando el torneo termina (con el podio sellado).</Aviso>
                    ) : null}
                    {chk ? (
                        <React.Fragment>
                            {chk.blocking.map((m, i) => <Aviso key={'b' + i} tipo="error">{m}</Aviso>)}
                            {chk.warnings.map((m, i) => <Aviso key={'w' + i} tipo="warn">{m}</Aviso>)}
                            {!chk.blocking.length && !chk.warnings.length ? <Aviso tipo="ok">Está todo para mandarla.</Aviso> : null}
                            <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                                <button type="button" className="btn btn-sm btn-accent" disabled={this.state.busy || chk.blocking.length > 0}
                                    onClick={() => this.bajarPlanilla('csv')}>Descargar para Excel (CSV)</button>
                                <button type="button" className="btn btn-sm btn-white" disabled={this.state.busy || chk.blocking.length > 0}
                                    onClick={() => this.bajarPlanilla('pdf')}>Descargar PDF</button>
                                <span className="text-muted" style={{ fontSize: 12 }}>Los datos que faltan se cargan en Configuración → Participantes → Editar.</span>
                            </div>
                        </React.Fragment>
                    ) : null}
                </div>
            </div>
        );
    }

    renderPosiciones() {
        const st = this.state.standings || [];
        const fr = this.state.finalRanking;
        const num = (n) => (n == null ? '—' : String(Math.round(Number(n) * 100) / 100).replace('.', ','));
        const chain = this.state.chain || [];
        const ties = this.state.ties || [];
        const pendientes = ties.filter((g) => !g.resolved);
        const stageBuilt = this.state.stageBuilt;

        const valor = {
            points: (f) => num(f.points),
            wins: (f) => f.wins,
            raw: (f) => num(f.raw),
            raw_share: (f) => `${String(Math.round((f.rawShare || 0) * 1000) / 10).replace('.', ',')}%`,
            seconds: (f) => f.seconds,
            thirds: (f) => f.thirds,
        };
        const columnas = [
            {
                k: 'r', t: '#', render: (f) => (
                    <span>{f.rank}{f.tied ? (
                        <span className={'badge ml-1 ' + (f.tieResolved ? 'badge-light' : 'badge-warning')}
                            title={f.tieResolved ? 'Empate ordenado por el juez' : 'Empate en toda la cadena: lo ordena el juez'}>
                            {f.tieResolved ? 'juez' : '='}
                        </span>
                    ) : null}</span>
                ),
            },
            { k: 'n', t: 'Jugador', left: true, render: (f) => (
                <span style={{ opacity: f.status === 'active' ? 1 : 0.55 }}>{f.name}{f.status === 'active' ? '' : ' (baja)'}</span>
            ) },
        ].concat(chain.map((c) => ({ k: c.key, t: (CRITERIOS[c.key] || {}).label || c.label, render: valor[c.key] || (() => '') })))
            .concat(chain.some((c) => c.key === 'raw') ? [] : (st.some((f) => f.raw) ? [{ k: 'raw', t: 'PV', render: valor.raw }] : []))
            .concat([{ k: 'ro', t: 'Rondas', render: (f) => f.rounds }])
            .concat(stageBuilt ? [{
                k: 'st', t: 'Etapa', render: (f) => (f.stage === stageBuilt
                    ? <span className="badge badge-primary">{stageBuilt === 'final' ? 'finalista' : 'semifinal'}</span>
                    : (f.stage ? <span className="text-muted" style={{ fontSize: 12 }}>{f.stage === 'semifinal' ? 'semifinal' : ''}</span> : null)),
            }] : []);

        return (
            <div>
                {pendientes.length ? (
                    <div className="card card-small mb-4" style={{ borderTop: '2px solid #FFB400' }}>
                        <div className="card-body">
                            <strong style={{ fontSize: 13.5 }}>Empates que sólo puede resolver el juez</strong>
                            <div className="text-muted" style={{ fontSize: 12.5, margin: '4px 0 10px' }}>
                                Empatan en toda la cadena ({chain.map((c) => (CRITERIOS[c.key] || {}).label || c.label).join(' → ')}).
                                Si uno de estos grupos cae en el corte de la final o la semifinal, la etapa no se arma hasta ordenarlo.
                            </div>
                            {pendientes.map((g) => (
                                <div key={g.participantIds.join('-')} className="d-flex align-items-center flex-wrap" style={{ gap: 10, marginBottom: 6, fontSize: 13 }}>
                                    <span>Puestos {g.fromRank} a {g.toRank}: {g.names.join(', ')}</span>
                                    <button type="button" className="btn btn-sm btn-accent" onClick={() => this.set({ modalEmpate: g })}>Resolver empate</button>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : null}
                <div className="row">
                    {fr ? (
                        <div className="col-lg-5">
                            {this.tablaPosiciones(fr, 'Clasificación final', [
                                { k: 'p', t: 'Puesto', render: (f) => f.position },
                                { k: 'n', t: 'Jugador', left: true, render: (f) => f.name },
                            ])}
                            <Aviso tipo="warn">
                                El podio sale del <strong>resultado de la mesa final</strong> (y de la semifinal, si hubo), no de la tabla.
                                Por eso los dos bloques van separados.
                            </Aviso>
                        </div>
                    ) : null}
                    <div className={fr ? 'col-lg-7' : 'col-lg-12'}>
                        {this.tablaPosiciones(st, fr ? 'Tabla de preliminares' : 'Tabla de posiciones', columnas)}
                        {ties.some((g) => g.resolved) ? (
                            <div className="text-muted" style={{ fontSize: 12, marginTop: -12, marginBottom: 16 }}>
                                “juez” = empate en toda la cadena que ordenó el juez. {ties.filter((g) => g.resolved).map((g) => (
                                    <button key={g.participantIds.join('-')} type="button" className="btn btn-sm btn-link p-0 ml-2"
                                        onClick={() => this.set({ modalEmpate: g })}>ver/cambiar {g.fromRank}–{g.toRank}</button>
                                ))}
                            </div>
                        ) : null}
                    </div>
                </div>
                {/* La tabla pública y el televisor se usan DURANTE el torneo; la planilla de Devir
                    recién cuando termina. Antes iba al revés y el "Agregar a la TV" quedaba al
                    fondo de todo, debajo de algo que ese día no se usa. */}
                {this.renderTablaPublica()}
                {this.renderPlanilla()}
            </div>
        );
    }

    // ───────────────────────────── shell

    render() {
        if (!this.state.checked) return (<div></div>);
        const t = this.state.tournament;
        const estado = t ? (ESTADO_TORNEO[t.status] || { label: t.status, badge: 'badge-secondary' }) : null;
        const TABS = [['config', 'Configuración'], ['rondas', 'Rondas y mesas'], ['posiciones', 'Posiciones']];

        return (
            <div className="container-fluid">
                <div className="row">
                    <SidebarComponent itemSelected={36} role={this.state.role} />
                    <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
                        <NavbarComponent role={this.state.role} />
                        <div className="main-content-container container-fluid px-4 mb-4">
                            <div className="d-flex justify-content-between align-items-center">
                                {/* minWidth:0 + flex:1 — sin esto el .row del header colapsa contra
                                    los botones y un título largo se parte en una palabra por línea. */}
                                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                    <HeaderComponent
                                        title={t && t.event_id ? 'Cafetería · Torneo del evento' : 'Cafetería · Torneos'}
                                        subtitle={t ? (t.title || 'Torneo') : 'Torneo'} />
                                </div>
                                <div className="text-nowrap" style={{ flex: '0 0 auto' }}>
                                    {estado ? <span className={'badge ' + estado.badge + ' mr-2'}>{estado.label}</span> : null}
                                    <button type="button" className="btn btn-sm btn-white" onClick={() => navigateTo('/tournaments')}>Volver</button>
                                </div>
                            </div>

                            {this.state.error ? (
                                <div className="card card-small mb-4"><div className="card-body" style={{ color: '#C4183C' }}>{this.state.error}</div></div>
                            ) : null}

                            {this.state.loading || !t ? (
                                <div className="card card-small mb-4"><div className="card-body text-center" style={{ padding: 40, color: '#818EA3' }}>Cargando…</div></div>
                            ) : (
                                <div>
                                    <div className="btn-group mb-4" role="group">
                                        {TABS.map(([k, label]) => (
                                            <button type="button" key={k}
                                                className={'btn ' + (this.state.tab === k ? 'btn-accent' : 'btn-white')}
                                                onClick={() => { this.tabElegido = k; this.setState({ tab: k }); }}>{label}</button>
                                        ))}
                                    </div>
                                    {this.renderModales()}
                                    {this.state.tab === 'config' ? this.renderConfig() : null}
                                    {this.state.tab === 'rondas' ? this.renderRondas() : null}
                                    {this.state.tab === 'posiciones' ? this.renderPosiciones() : null}
                                </div>
                            )}
                        </div>
                    </main>
                </div>
            </div>
        );
    }
}

export default TournamentPage;
